import React, { useState } from "react";
import {
  Stack,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import axios from "axios";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

import { useAppDispatch } from "../redux/store";
import { createBrand, updateBrand } from "../redux/actions/brandActions";
import { cleanFormData } from "../helpers/formHelpers";

import { BrandRequestDto } from "../shared/dtos/brand/BrandRequestDto";
import { BrandResponseDto } from "../shared/dtos/brand/BrandResponseDto";
import { brandActions } from "../redux/slices/brand";
import { getErrorMessage } from "../utils/appUtils";

interface CreateOrUpdateBrandFormProps {
  brand?: BrandResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const brandSchema = Yup.object().shape({
  name: Yup.string().required("Brand name is required"),
  logoUrl: Yup.string().url("Must be a valid URL").nullable(),
});

const CreateOrUpdateBrandForm: React.FC<CreateOrUpdateBrandFormProps> = ({
  brand = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>("");

  const initialValues = {
    name: brand?.name || "",
    logoUrl: brand?.logoUrl || "",
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string) => void,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setUploadError("File is too large. Max size is 1MB.");
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "GN_Products");

      const { data } = await axios.post<{ secure_url: string }>(
        "https://api.cloudinary.com/v1_1/dsiyinydr/image/upload",
        formData,
      );

      if (data.secure_url) {
        setFieldValue("logoUrl", data.secure_url);
      }
    } catch (err) {
      setUploadError(
        getErrorMessage(err, "Failed to upload image. Try again."),
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(brandActions.setErrorUpdate(null))
            : () => dispatch(brandActions.setErrorCreate(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={brandSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values) as BrandRequestDto;
          if (isEditing && brand) {
            dispatch(updateBrand({ id: brand.id, values: cleanedValues }));
          } else {
            dispatch(createBrand(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({
          dirty,
          isValid,
          values,
          setFieldValue,
        }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={3}>
              <FormTextField
                name="name"
                label="Brand Name"
                placeholder="e.g. Coca-Cola"
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  color="text.secondary"
                >
                  Brand Logo
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ alignItems: "center" }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      bgcolor: "grey.50",
                    }}
                  >
                    {values.logoUrl ? (
                      <img
                        src={values.logoUrl}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        No Logo
                      </Typography>
                    )}
                  </Box>

                  <Stack spacing={1} sx={{ flex: 1, width: "100%" }}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={
                          uploading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                        disabled={uploading}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        Upload Logo (Max 1MB)
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, setFieldValue)}
                        />
                      </Button>
                      {values.logoUrl && (
                        <IconButton
                          color="error"
                          onClick={() => setFieldValue("logoUrl", "")}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>

                    <Divider>
                      <Typography variant="caption" color="text.disabled">
                        OR PASTE URL
                      </Typography>
                    </Divider>

                    <FormTextField
                      name="logoUrl"
                      label="Logo URL"
                      placeholder="https://example.com/logo.png"
                      size="small"
                    />
                  </Stack>
                </Stack>
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {uploadError}
                  </Alert>
                )}
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Brand"}
                loading={loading}
                disabled={!dirty || !isValid || loading || uploading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateBrandForm;
